import React, { useMemo } from 'react'
import { GiftedCouponListSpace, PurchaseHistoryListSpace } from 'src/types/api'
import { Container } from 'src/components/common/Container'
import { Typography } from 'src/components/common/Typography'
import { Roboto_num3_r14 } from 'src/styles/fonts/RobotoFonts'
import { Chips } from 'src/components/common/Chips'
import { Noto_body1_b16, Noto_body2_m14 } from 'src/styles/fonts/NotoSansKrFonts'
import { SmallDivider } from 'src/components/common/Divider'
import { PurchaseHistoryPageProps } from 'src/pages/PurchaseHistoryPage'
import { DateUtils } from 'src/utils/date'
import styled, { css } from 'styled-components'
import CouponCircleImage from 'src/components/common/CouponCircleImage'
import RenderIf from 'src/components/common/RenderIf'
import { useGlobalStore } from 'src/store/zustand'
import { getBrandAndCouponName } from 'src/utils'
import { useModal } from 'src/provider/ModalContextProvider'
import usePageNav from 'src/hooks/usePageNav.hook'

interface CouponCardInfoData {
  couponName: string
  couponImage: string
  couponType?: number
  status: number
  expireDT?: string
  regDT: string
  onClickFn: () => void
}

const PurchaseHistoryCouponCard = ({
  couponInfo,
  itemsOfStatus,
  type = 'default',
}: Pick<PurchaseHistoryPageProps, 'itemsOfStatus'> & {
  couponInfo:
    | PurchaseHistoryListSpace.PurchaseListInterface
    | GiftedCouponListSpace.GiftedCouponListInterface
  type?: 'default' | 'gift'
}) => {
  const { getDayDifference, DateFormat, expiration7Days } = DateUtils
  const { purchaseHistoryUnderLineTab, maintenanceBlock } = useGlobalStore()
  const { openModal } = useModal()
  const { purchaseHistoryDetailPageNav, giftedCouponDetailPageNav } = usePageNav()

  const renderData = (type: 'default' | 'gift') => {
    if (type === 'gift') {
      const propsInfo = couponInfo as GiftedCouponListSpace.GiftedCouponListInterface
      const { GiftIdx, CouponName, CouponImage, GiftStatus, RegDT } = propsInfo
      return {
        couponName: CouponName,
        couponImage: CouponImage,
        status: GiftStatus,
        regDT: RegDT,
        onClickFn: () => giftedCouponDetailPageNav(GiftIdx),
      }
    } else {
      const propsInfo = couponInfo as PurchaseHistoryListSpace.PurchaseListInterface
      const {
        Idx,
        CouponIdx,
        CouponNumber,
        CouponName,
        CouponType,
        CouponImage,
        Status,
        ExpireDT,
        RegDT,
      } = propsInfo
      return {
        couponName: CouponName,
        couponImage: CouponImage,
        couponType: CouponType,
        status: Status,
        expireDT: ExpireDT,
        regDT: RegDT,
        onClickFn: () =>
          purchaseHistoryDetailPageNav(Idx, CouponIdx, `${Status}`, CouponType, CouponNumber),
      }
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const couponCardInfoData: CouponCardInfoData = useMemo(() => renderData(type), [couponInfo])
  const { couponName, couponImage, couponType, status, regDT, expireDT, onClickFn } =
    couponCardInfoData

  return (
    <S.PurchaseHistoryCouponCard.Container
      $option="flex_column"
      onClick={() => {
        if (!!maintenanceBlock) {
          openModal({
            contents: { t: maintenanceBlock.t, d: maintenanceBlock.d },
            button: { submit: { t: '확인' } },
          })
          return
        }
        onClickFn()
      }}
    >
      <Container $option="flex_row_space_between">
        <S.PurchaseHistoryCouponCard.Date $option="flex_row" $width="fit-content">
          <Typography $fontStyle={Roboto_num3_r14} $color="var(--black-50)">
            {DateFormat(regDT, 'YY.MM.DD')} {type === 'gift' ? '선물' : '구매'}
          </Typography>
          <RenderIf when={purchaseHistoryUnderLineTab === 0 && expireDT !== undefined}>
            <Chips.Item
              size="small"
              color={`var(${expiration7Days(expireDT!) ? '--black-50' : '--red-50'})`}
            >
              D-{getDayDifference(expireDT!, 'days')}
            </Chips.Item>
          </RenderIf>
        </S.PurchaseHistoryCouponCard.Date>
        <Typography $fontStyle={Noto_body2_m14} $color={itemsOfStatus[status]?.color}>
          {itemsOfStatus[status]?.text}
        </Typography>
      </Container>
      <SmallDivider />
      <S.PurchaseHistoryCouponCard.Contents.Container
        $option="flex_row_space_between"
        $column_gap="16px"
      >
        <CouponCircleImage src={couponImage} />
        <Container $option="flex_column" $row_gap="2px">
          <S.PurchaseHistoryCouponCard.Contents.Top $option="flex_row" $column_gap="2px">
            <Typography $fontStyle={Noto_body2_m14} $color="var(--black-50)">
              {getBrandAndCouponName(couponName)?.brandName}
            </Typography>
            <RenderIf when={couponType !== undefined}>
              <Chips.Item size="little" style={S.PurchaseHistoryCouponCard.Contents.ChipsStyle}>
                {couponType === 0 ? '상시할인' : '핫딜'}
              </Chips.Item>
            </RenderIf>
          </S.PurchaseHistoryCouponCard.Contents.Top>
          <Typography $fontStyle={Noto_body1_b16}>
            {getBrandAndCouponName(couponName)?.couponName}
          </Typography>
        </Container>
      </S.PurchaseHistoryCouponCard.Contents.Container>
    </S.PurchaseHistoryCouponCard.Container>
  )
}
export default PurchaseHistoryCouponCard

const S = {
  PurchaseHistoryCouponCard: {
    Container: styled(Container)`
      padding: 20px 20px 30px 20px;
      gap: 16px;
      background-color: var(--white);
    `,
    Date: styled(Container)`
      width: fit-content;
      gap: 6px;
      align-items: center;
    `,
    Contents: {
      Container: styled(Container)`
        cursor: pointer;
      `,
      Top: styled(Container)`
        align-items: center;
      `,
      ChipsStyle: css`
        background: var(--black-4);
      `,
    },
  },
}
